import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiInfo, FiChevronLeft, FiChevronRight, FiCheck, FiRefreshCw } from 'react-icons/fi';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import { validatePhoneForCountry, parsePhoneWithCountryCode } from '../../utils/phoneValidation';
import { organizationsApi, lookupsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Swal from 'sweetalert2';

const initialFormState = {
  name: '',
  category: '',
  description: '',
  businessUnit: '',
  customerOwner: '',
  riskAssessmentContact: '',
  riskAssessmentContactCountry: 'US',
  riskRating: 0,
  status: 0,
  archived: 0,
  notes: '',
  legalHoldStatus: 0,
  primaryContact: '',
  primaryEmail: '',
  primaryPhone: '',
  primaryPhoneCountry: 'US',
  backupContact: '',
  backupEmail: '',
  backupPhone: '',
  logoUrl: '',
  numOfEmployees: null,
  backupPhoneCountry: 'US',
  certificationDate: '',
  maturityScore: 0,
  targetMaturityScore: 0,
  inherentRiskScore: 0,
  versionId: null,
  industryId: null,
  segmentId: null,
  tierRating: 0,
  profile: '',
  onboardingStatus: 0,
  complianceStatus: 0
};

const Organizations = () => {
  const { role, user } = useAuth();
  // Only the enterprise `admin` role sees every organization.
  // `assessmentadmin` and `departmentadmin` are scoped to their own organization.
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isEnterpriseAdmin = role === 'admin' || (roles.includes('admin') &&
    !roles.includes('assessmentadmin') &&
    !roles.includes('departmentadmin') &&
    !roles.includes('department_admin'));
  const isScopedAdmin = !isEnterpriseAdmin && (
    role === 'assessmentadmin' ||
    role === 'departmentadmin' ||
    role === 'department_admin' ||
    roles.includes('assessmentadmin') ||
    roles.includes('departmentadmin') ||
    roles.includes('department_admin')
  );
  const isAssessmentAdmin = isScopedAdmin;
  // Only the enterprise `admin` role may restore a deactivated organization (matches the
  // API's admin-only check). Everyone else can only wait out the 30-day auto-deletion.
  const canRestore = roles.some(r => typeof r === 'string' && r.toLowerCase() === 'admin');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Lookup data
  const [options, setOptions] = useState({});
  const [industries, setIndustries] = useState([]);
  const [segments, setSegments] = useState([]);
  const [versions, setVersions] = useState([]);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState([]);
  // 'active' shows live organizations; 'deactivated' shows soft-deleted ones awaiting purge.
  const [viewMode, setViewMode] = useState('active');

  // Toast notification
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Fetch organizations from API
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await organizationsApi.getAll(1, 10000, null, null, null, viewMode === 'deactivated');
      let data = response.data;
      // assessmentadmin / departmentadmin can only see their own organization
      console.log('[Organizations] role:', role, 'roles:', user?.roles, 'organization:', user?.organization, 'isScopedAdmin:', isAssessmentAdmin);
      if (isAssessmentAdmin && user?.organization) {
        const userOrg = user.organization.toLowerCase().trim();
        data = data.filter(org => org.name?.toLowerCase().trim() === userOrg);
        console.log('[Organizations] Filtered to', data.length, 'orgs for scoped admin org:', user.organization);
      }
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [isAssessmentAdmin, user?.organization, viewMode]);

  // Fetch lookup data
  const fetchLookups = useCallback(async () => {
    try {
      const [optionsRes, industriesRes, versionsRes] = await Promise.all([
        lookupsApi.getOptions(),
        lookupsApi.getIndustries(),
        lookupsApi.getVersions()
      ]);
      setOptions(optionsRes);
      setIndustries(industriesRes);
      setVersions(versionsRes);
    } catch (err) {
      console.error('Failed to fetch lookups:', err);
    }
  }, []);

  // Fetch segments when industry changes
  const fetchSegments = useCallback(async (industryId) => {
    if (!industryId) {
      setSegments([]);
      return;
    }
    try {
      const segmentsRes = await lookupsApi.getSegments(industryId);
      setSegments(segmentsRes);
    } catch (err) {
      console.error('Failed to fetch segments:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchLookups();
  }, [fetchOrganizations, fetchLookups]);

  useEffect(() => {
    if (formData.industryId) {
      fetchSegments(formData.industryId);
    }
  }, [formData.industryId, fetchSegments]);

  const getStatusText = (value) => {
    const item = options.statusList?.find(s => s.value === value);
    return item?.text || 'Unknown';
  };

  const getRiskRatingText = (value) => {
    const item = options.riskRatingList?.find(s => s.value === value);
    return item?.text || 'Unknown';
  };

  // Checkbox helpers
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (selectedIds.length === organizations.length) setSelectedIds([]);
    else setSelectedIds(organizations.map(o => o.id));
  };
  const isAllSelected = organizations.length > 0 && selectedIds.length === organizations.length;

  const openCreateModal = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setCurrentStep(0);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (org) => {
    setFormData({
      name: org.name || '',
      category: org.category || '',
      description: org.description || '',
      businessUnit: org.businessUnit || '',
      customerOwner: org.customerOwner || '',
      riskAssessmentContact: org.riskAssessmentContact || '',
      riskAssessmentContactCountry: org.riskAssessmentContactCountry || parsePhoneWithCountryCode(org.riskAssessmentContact).countryCode || 'US',
      riskRating: org.riskRating || 0,
      status: org.status || 0,
      archived: org.archived || 0,
      notes: org.notes || '',
      legalHoldStatus: org.legalHoldStatus || 0,
      primaryContact: org.primaryContact || '',
      primaryEmail: org.primaryEmail || '',
      primaryPhone: org.primaryPhone || '',
      primaryPhoneCountry: org.primaryPhoneCountry || parsePhoneWithCountryCode(org.primaryPhone).countryCode || 'US',
      backupContact: org.backupContact || '',
      backupEmail: org.backupEmail || '',
      backupPhone: org.backupPhone || '',
      backupPhoneCountry: org.backupPhoneCountry || parsePhoneWithCountryCode(org.backupPhone).countryCode || 'US',
      certificationDate: org.certificationDate ? new Date(org.certificationDate).toISOString().split('T')[0] : '',
      maturityScore: org.maturityScore || 0,
      targetMaturityScore: org.targetMaturityScore || 0,
      inherentRiskScore: org.inherentRiskScore || 0,
      versionId: org.versionId || null,
      industryId: org.industryId || null,
      segmentId: org.segmentId || null,
      tierRating: org.tierRating || 0,
      profile: org.profile || '',
      onboardingStatus: org.onboardingStatus || 0,
      complianceStatus: org.complianceStatus || 0
    });
    setSelectedOrg(org);
    setIsEditing(true);
    setCurrentStep(0);
    setValidationErrors({});
    if (org.industryId) {
      fetchSegments(org.industryId);
    }
    setIsModalOpen(true);
  };

  const openDeleteModal = (org) => {
    console.log('Opening delete modal for:', org);
    if (!org || !org.id) {
      alert('Invalid organization data');
      return;
    }
    setSelectedOrg(org);
    setIsDeleteModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' || name.endsWith('Id') || name.endsWith('Rating') || name.endsWith('Score') || name.endsWith('Status') || name === 'archived'
        ? (value === '' ? null : parseInt(value, 10)) 
        : value 
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone, countryCode = 'US') => {
    const result = validatePhoneForCountry(phone, countryCode);
    return result;
  };

  const handlePhoneChange = (name, fullValue, countryCode) => {
    const countryFieldMap = {
      primaryPhone: 'primaryPhoneCountry',
      backupPhone: 'backupPhoneCountry',
      riskAssessmentContact: 'riskAssessmentContactCountry'
    };
    const countryFieldName = countryFieldMap[name];
    setFormData(prev => ({
      ...prev,
      [name]: fullValue,
      [countryFieldName]: countryCode
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // Basic Info
        if (!formData.name || formData.name.trim() === '') {
          errors.name = 'Organization name is required';
        }
        if (!formData.category || formData.category.trim() === '') {
          errors.category = 'Category is required';
        }
        if (!formData.description || formData.description.trim() === '') {
          errors.description = 'Description is required';
        }
        if (!formData.businessUnit || formData.businessUnit.trim() === '') {
          errors.businessUnit = 'Business unit is required';
        }
        if (!formData.customerOwner || formData.customerOwner.trim() === '') {
          errors.customerOwner = 'Customer owner is required';
        }
        if (!formData.riskAssessmentContact || formData.riskAssessmentContact.trim() === '') {
          errors.riskAssessmentContact = 'Risk assessment contact is required';
        }
        break;
      case 1: // Contacts
        if (!formData.primaryContact || formData.primaryContact.trim() === '') {
          errors.primaryContact = 'Primary contact name is required';
        }
        if (!formData.primaryEmail || formData.primaryEmail.trim() === '') {
          errors.primaryEmail = 'Primary email is required';
        } else if (!validateEmail(formData.primaryEmail)) {
          errors.primaryEmail = 'Please enter a valid email address';
        }
        if (!formData.primaryPhone || formData.primaryPhone.trim() === '') {
          errors.primaryPhone = 'Primary phone is required';
        } else {
          const phoneResult = validatePhone(formData.primaryPhone, formData.primaryPhoneCountry);
          if (!phoneResult.valid) {
            errors.primaryPhone = phoneResult.message;
          }
        }
        if (!formData.backupContact || formData.backupContact.trim() === '') {
          errors.backupContact = 'Backup contact name is required';
        }
        if (!formData.backupEmail || formData.backupEmail.trim() === '') {
          errors.backupEmail = 'Backup email is required';
        } else if (!validateEmail(formData.backupEmail)) {
          errors.backupEmail = 'Please enter a valid email address';
        }
        if (!formData.backupPhone || formData.backupPhone.trim() === '') {
          errors.backupPhone = 'Backup phone is required';
        } else {
          const phoneResult = validatePhone(formData.backupPhone, formData.backupPhoneCountry);
          if (!phoneResult.valid) {
            errors.backupPhone = phoneResult.message;
          }
        }
        break;
      case 2: // Classification - Optional fields, no validation needed
        break;
      case 3: // Risk & Compliance - Optional fields, no validation needed
        if (formData.inherentRiskScore !== null && formData.inherentRiskScore !== '' && (formData.inherentRiskScore < 0 || formData.inherentRiskScore > 100)) {
          errors.inherentRiskScore = 'Score must be between 0 and 100';
        }
        break;
      default:
        break;
    }
    
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setCurrentStep(s => s + 1);
  };

  const handleSubmit = async () => {
    // Validate all steps before submitting
    let allErrors = {};
    for (let i = 0; i < formSteps.length; i++) {
      const stepErrors = validateStep(i);
      if (Object.keys(stepErrors).length > 0) {
        allErrors = { ...allErrors, ...stepErrors };
      }
    }
    
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      // Find first step with errors and navigate to it
      for (let i = 0; i < formSteps.length; i++) {
        const stepErrors = validateStep(i);
        if (Object.keys(stepErrors).length > 0) {
          setCurrentStep(i);
          break;
        }
      }
      alert('Please fill in all required fields');
      return;
    }

    const submitData = {
      ...formData,
      certificationDate: formData.certificationDate || null
    };
    
    const currentIsEditing = isEditing;
    const currentOrgId = selectedOrg?.id;
    
    // Optimistic UI update
    if (currentIsEditing) {
      // Update existing organization in the list
      setOrganizations(prev => prev.map(org => 
        org.id === currentOrgId 
          ? {
              ...org,
              ...submitData,
              industryName: industries.find(i => i.industryId === submitData.industryId)?.industryName || org.industryName,
              segmentName: segments.find(s => s.segmentId === submitData.segmentId)?.segmentName || org.segmentName
            }
          : org
      ));
    } else {
      // Add new organization to the list immediately
      const tempOrg = {
        id: Date.now(), // Temporary ID
        ...submitData,
        industryName: industries.find(i => i.industryId === submitData.industryId)?.industryName || null,
        segmentName: segments.find(s => s.segmentId === submitData.segmentId)?.segmentName || null
      };
      setOrganizations(prev => [tempOrg, ...prev]);
    }
    
    // Close modal immediately
    setIsModalOpen(false);
    setFormData(initialFormState);
    setCurrentStep(0);
    setValidationErrors({});
    setSelectedOrg(null);
    setIsEditing(false);

    // Perform API call in background
    try {
      if (currentIsEditing) {
        await organizationsApi.update(currentOrgId, submitData);
        showToast('Organization updated successfully', 'success');
      } else {
        await organizationsApi.create(submitData);
        showToast('Organization created successfully', 'success');
      }
      
      // Refresh to get real data from server
      await fetchOrganizations();
      
    } catch (err) {
      console.error('Failed to save organization:', err);
      showToast('Failed to save organization: ' + (err.response?.data?.message || err.message), 'error');
      // Refresh to restore correct state
      await fetchOrganizations();
    }
  };

  const handleDelete = async () => {
    if (!selectedOrg || !selectedOrg.id) {
      alert('No organization selected for deletion');
      return;
    }

    // Store the ID before clearing state
    const deletedOrgId = selectedOrg.id;
    
    // Close modal immediately for better UX
    setIsDeleteModalOpen(false);
    setSelectedOrg(null);

    try {
      console.log('Deleting organization:', deletedOrgId);
      
      // Clear from selected IDs if it was selected
      setSelectedIds(prev => prev.filter(id => id !== deletedOrgId));
      
      // Remove from local state immediately for instant UI feedback
      setOrganizations(prev => prev.filter(org => org.id !== deletedOrgId));
      
      // Delete from API
      await organizationsApi.delete(deletedOrgId);
      
      // Refresh data from server to ensure consistency
      await fetchOrganizations();
      
      showToast('Organization deleted successfully', 'success');
    } catch (err) {
      console.error('Failed to delete organization:', err);
      showToast('Failed to delete organization: ' + (err.message || 'Unknown error'), 'error');
      // Refresh data in case of error to restore correct state
      fetchOrganizations();
    }
  };

  const handleRestore = async (org) => {
    if (!org || !org.id) return;
    try {
      // Remove from the deactivated list immediately for instant feedback
      setOrganizations(prev => prev.filter(o => o.id !== org.id));
      await organizationsApi.restore(org.id);
      await fetchOrganizations();
      showToast('Organization restored successfully', 'success');
    } catch (err) {
      console.error('Failed to restore organization:', err);
      showToast('Failed to restore organization: ' + (err.message || 'Unknown error'), 'error');
      fetchOrganizations();
    }
  };

  const handleTrustCenterToggle = async (org) => {
    const newEnabled = !org.trustCenterEnabled;
    const actionText = newEnabled ? 'enable' : 'disable';

    const result = await Swal.fire({
      title: `${newEnabled ? 'Enable' : 'Disable'} Trust Center?`,
      text: `Are you sure you want to ${actionText} Trust Center for "${org.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0077b5',
      cancelButtonColor: '#919191',
      confirmButtonText: `Yes, ${actionText} it`,
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#53545a',
      iconColor: '#F47F2E',
    });

    if (result.isConfirmed) {
      // Optimistic UI update
      setOrganizations(prev =>
        prev.map(o => o.id === org.id ? { ...o, trustCenterEnabled: newEnabled } : o)
      );

      try {
        await organizationsApi.toggleTrustCenter(org.id, newEnabled);
        showToast(`Trust Center ${actionText}d for ${org.name}`, 'success');
        // await fetchOrganizations();
      } catch (err) {
        console.error('Failed to toggle Trust Center:', err);
        showToast('Failed to update Trust Center status: ' + (err.message || 'Unknown error'), 'error');
        // Revert on failure
        setOrganizations(prev =>
          prev.map(o => o.id === org.id ? { ...o, trustCenterEnabled: !newEnabled } : o)
        );
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      1: 'bg-[#0077b5]/20 text-[#0077b5]',
      2: 'bg-[#919191]/20 text-[#53545a]',
      3: 'bg-[#F47F2E]/20 text-[#F47F2E]',
      0: 'bg-[#919191]/20 text-[#919191]'
    };
    return styles[status] || styles[0];
  };

  const getRiskBadge = (risk) => {
    const styles = {
      4: 'bg-[#0077b5]/20 text-[#0077b5]',
      3: 'bg-[#919191]/20 text-[#919191]',
      2: 'bg-[#F47F2E]/20 text-[#F47F2E]',
      1: 'bg-[#53545a]/20 text-[#53545a]',
      0: 'bg-[#919191]/20 text-[#919191]'
    };
    return styles[risk] || styles[0];
  };

  // Column definitions for DataTable
  const columns = useMemo(() => [
    !isAssessmentAdmin && {
      key: 'select',
      header: (
        <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll}
          className="w-4 h-4 rounded border-[#919191]/40 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer" />
      ),
      render: (row) => (
        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)}
          className="w-4 h-4 rounded border-[#919191]/40 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer" />
      ),
      headerClassName: 'w-10',
    },
    {
      key: 'entity',
      header: 'Entity',
      sortable: true,
      filterable: true,
      accessor: (row) => `${row.name || ''} ${row.businessUnit || ''}`,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {row.name?.charAt(0) || 'O'}
          </div>
          <div>
            <span className="text-sm font-medium text-[#53545a]">{row.name}</span>
            <p className="text-xs text-[#919191]">{row.businessUnit}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      filterable: true,
      accessor: (row) => row.category || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.category || '-'}</span>,
    },
    {
      key: 'customerOwner',
      header: 'Customer Owner',
      sortable: true,
      filterable: true,
      accessor: (row) => row.customerOwner || '',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {row.customerOwner?.charAt(0) || '?'}
          </div>
          <span className="text-sm text-[#53545a]">{row.customerOwner || '-'}</span>
        </div>
      ),
    },
    {
      key: 'industry',
      header: 'Industry',
      sortable: true,
      filterable: true,
      accessor: (row) => row.industryName || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.industryName || '-'}</span>,
    },
    {
      key: 'riskRating',
      header: 'Risk Rating',
      sortable: true,
      filterable: true,
      accessor: (row) => getRiskRatingText(row.riskRating),
      render: (row) => (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getRiskBadge(row.riskRating)}`}>
          {getRiskRatingText(row.riskRating)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      filterable: true,
      accessor: (row) => getStatusText(row.status),
      render: (row) => (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(row.status)}`}>
          {getStatusText(row.status)}
        </span>
      ),
    },
    !isAssessmentAdmin && {
      key: 'trustCenter',
      header: 'Trust Center',
      headerClassName: 'text-center',
      render: (row) => (
        <div className="flex justify-center">
          <button
            onClick={() => handleTrustCenterToggle(row)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#0077b5] focus:ring-offset-2 ${
              row.trustCenterEnabled ? 'bg-[#0077b5]' : 'bg-[#919191]/40'
            }`}
            role="switch"
            aria-checked={!!row.trustCenterEnabled}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
              row.trustCenterEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      ),
    },
    viewMode === 'deactivated' && {
      key: 'autoDelete',
      header: 'Auto-delete',
      render: (row) => {
        const days = row.daysUntilDeletion;
        if (days === null || days === undefined) return <span className="text-[#919191]">—</span>;
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            days <= 7 ? 'bg-red-100 text-red-700' : 'bg-[#F47F2E]/15 text-[#e06a1f]'
          }`}>
            {days === 0 ? 'Deletes today' : `Deletes in ${days} day${days === 1 ? '' : 's'}`}
          </span>
        );
      },
    },
  ].filter(Boolean), [isAllSelected, selectedIds, options, isAssessmentAdmin, viewMode]);

  // Action menu items per row
  const getActions = useCallback((org) => {
    if (viewMode === 'deactivated') {
      // Restore is admin-only; non-admins see no actions and must wait for auto-deletion.
      return canRestore
        ? [{ label: 'Restore', icon: FiRefreshCw, onClick: () => handleRestore(org) }]
        : [];
    }
    return [
      { label: 'Edit', icon: FiEdit2, onClick: () => openEditModal(org) },
      { divider: true },
      { label: 'Deactivate', icon: FiTrash2, onClick: () => openDeleteModal(org), danger: true },
    ];
  }, [viewMode, canRestore]);

  // Form steps for wizard
  const formSteps = [
    { title: 'Basic Info', description: 'Organization details' },
    { title: 'Contacts', description: 'Primary & backup contacts' },
    { title: 'Classification', description: 'Industry & segment' },
    { title: 'Risk & Compliance', description: 'Ratings and status' }
  ];

  const inputClass = (fieldName) => `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5] focus:border-transparent ${validationErrors[fieldName] ? 'border-[#F47F2E]' : ''} bg-white border-[#919191]/30 text-[#53545a]`;
  const labelClass = `block text-sm font-medium mb-2 text-[#53545a]`;
  const errorClass = 'text-[#F47F2E] text-xs mt-1';

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Organization Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={inputClass('name')} placeholder="Enter organization name" />
              {validationErrors.name && <p className={errorClass}>{validationErrors.name}</p>}
            </div>
            <div>
              <label className={labelClass}>Category *</label>
              <input type="text" name="category" value={formData.category} onChange={handleInputChange} className={inputClass('category')} placeholder="Enter category" />
              {validationErrors.category && <p className={errorClass}>{validationErrors.category}</p>}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description *</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className={`${inputClass('description')} resize-none`} placeholder="Enter description" />
              {validationErrors.description && <p className={errorClass}>{validationErrors.description}</p>}
            </div>
            <div>
              <label className={labelClass}>Business Unit *</label>
              <input type="text" name="businessUnit" value={formData.businessUnit} onChange={handleInputChange} className={inputClass('businessUnit')} placeholder="Enter business unit" />
              {validationErrors.businessUnit && <p className={errorClass}>{validationErrors.businessUnit}</p>}
            </div>
            <div>
              <label className={labelClass}>Customer Owner *</label>
              <input type="text" name="customerOwner" value={formData.customerOwner} onChange={handleInputChange} className={inputClass('customerOwner')} placeholder="Enter customer owner" />
              {validationErrors.customerOwner && <p className={errorClass}>{validationErrors.customerOwner}</p>}
            </div>
            <div>
              <label className={labelClass}>Risk Assessment Contact *</label>
              <input
                type="text"
                name="riskAssessmentContact"
                value={formData.riskAssessmentContact}
                onChange={handleInputChange}
                className={inputClass('riskAssessmentContact')}
                placeholder="Enter risk assessment contact"
              />
              {validationErrors.riskAssessmentContact && (
                <p className={errorClass}>{validationErrors.riskAssessmentContact}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Profile</label>
              <input type="text" name="profile" value={formData.profile} onChange={handleInputChange} className={inputClass('profile')} placeholder="Enter profile" />
            </div>
            <div>
              <label className={labelClass}>Logo URL</label>
              <input type="url" name="logoUrl" value={formData.logoUrl} onChange={handleInputChange} className={inputClass('logoUrl')} placeholder="https://example.com/logo.png" />
            </div>
            <div>
              <label className={labelClass}>Number of Employees</label>
              <input type="number" name="numOfEmployees" value={formData.numOfEmployees || ''} onChange={handleInputChange} className={inputClass('numOfEmployees')} placeholder="Enter employee count" min="0" />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2} className={`${inputClass('notes')} resize-none`} placeholder="Additional notes" />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <h4 className={`text-lg font-medium mb-4 text-[#53545a]`}>Primary Contact</h4>
            </div>
            <div>
              <label className={labelClass}>Contact Name *</label>
              <input type="text" name="primaryContact" value={formData.primaryContact} onChange={handleInputChange} className={inputClass('primaryContact')} placeholder="Primary contact name" />
              {validationErrors.primaryContact && <p className={errorClass}>{validationErrors.primaryContact}</p>}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" name="primaryEmail" value={formData.primaryEmail} onChange={handleInputChange} className={inputClass('primaryEmail')} placeholder="email@company.com" />
              {validationErrors.primaryEmail && <p className={errorClass}>{validationErrors.primaryEmail}</p>}
            </div>
            <div>
              <CountryPhoneInput
                name="primaryPhone"
                value={formData.primaryPhone}
                countryCodeValue={formData.primaryPhoneCountry}
                onChange={handlePhoneChange}
                error={validationErrors.primaryPhone}
                required
                label=" Primary Phone"
              />
            </div>
            <div className="md:col-span-2 border-t border-[#919191]/30 pt-4 mt-2">
              <h4 className={`text-lg font-medium mb-4 text-[#53545a]`}>Backup Contact</h4>
            </div>
            <div>
              <label className={labelClass}>Contact Name *</label>
              <input type="text" name="backupContact" value={formData.backupContact} onChange={handleInputChange} className={inputClass('backupContact')} placeholder="Backup contact name" />
              {validationErrors.backupContact && <p className={errorClass}>{validationErrors.backupContact}</p>}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" name="backupEmail" value={formData.backupEmail} onChange={handleInputChange} className={inputClass('backupEmail')} placeholder="backup@company.com" />
              {validationErrors.backupEmail && <p className={errorClass}>{validationErrors.backupEmail}</p>}
            </div>
            <div>
              <CountryPhoneInput
                name="backupPhone"
                value={formData.backupPhone}
                countryCodeValue={formData.backupPhoneCountry}
                onChange={handlePhoneChange}
                error={validationErrors.backupPhone}
                required
                label=" Backup Phone"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Industry</label>
              <select name="industryId" value={formData.industryId || ''} onChange={handleInputChange} className={inputClass('industryId')}>
                <option value="">Select Industry</option>
                {industries.map(ind => (
                  <option key={ind.industryId} value={ind.industryId}>{ind.industryName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Segment</label>
              <select name="segmentId" value={formData.segmentId || ''} onChange={handleInputChange} className={inputClass('segmentId')} disabled={!formData.industryId}>
                <option value="">Select Segment</option>
                {segments.map(seg => (
                  <option key={seg.segmentId} value={seg.segmentId}>{seg.segmentName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Version</label>
              <select name="versionId" value={formData.versionId || ''} onChange={handleInputChange} className={inputClass('versionId')}>
                <option value="">Select Version</option>
                {versions.map(ver => (
                  <option key={ver.versionId} value={ver.versionId}>{ver.versionName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Certification Date</label>
              <input type="date" name="certificationDate" value={formData.certificationDate} onChange={handleInputChange} className={inputClass('certificationDate')} />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange} className={inputClass('status')}>
                {options.statusList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Risk Rating</label>
              <select name="riskRating" value={formData.riskRating} onChange={handleInputChange} className={inputClass('riskRating')}>
                {options.riskRatingList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                Tier Rating
                <span className="ml-1 text-xs text-[#919191]" title="Tier 1 (High Risk): Vendors with significant impact. Tier 2 (Moderate Risk): Regular monitoring needed. Tier 3 (Low Risk): Minimal impact.">
                  <FiInfo className="inline" />
                </span>
              </label>
              <select name="tierRating" value={formData.tierRating} onChange={handleInputChange} className={inputClass('tierRating')}>
                {options.tierRatingList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Compliance Status</label>
              <select name="complianceStatus" value={formData.complianceStatus} onChange={handleInputChange} className={inputClass('complianceStatus')}>
                {options.complianceStatusList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Onboarding Status</label>
              <select name="onboardingStatus" value={formData.onboardingStatus} onChange={handleInputChange} className={inputClass('onboardingStatus')}>
                {options.onboardingStatusList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                Maturity Score
                <span className="ml-1 text-xs text-[#919191]" title="Initial (Ad Hoc): Chaotic, undocumented. Repeatable: Basic processes. Defined: Standardized. Managed: Measured. Optimized: Continuous improvement.">
                  <FiInfo className="inline" />
                </span>
              </label>
              <select name="maturityScore" value={formData.maturityScore} onChange={handleInputChange} className={inputClass('maturityScore')}>
                {options.maturityList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Target Maturity Score</label>
              <select name="targetMaturityScore" value={formData.targetMaturityScore} onChange={handleInputChange} className={inputClass('targetMaturityScore')}>
                {options.maturityList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Inherent Risk Score</label>
              <input type="number" name="inherentRiskScore" value={formData.inherentRiskScore || ''} onChange={handleInputChange} className={inputClass('inherentRiskScore')} placeholder="0-100" min="0" max="100" />
              {validationErrors.inherentRiskScore && <p className={errorClass}>{validationErrors.inherentRiskScore}</p>}
            </div>
            <div>
              <label className={labelClass}>Archival Status</label>
              <select name="archived" value={formData.archived} onChange={handleInputChange} className={inputClass('archived')}>
                {options.archivalStatusList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Legal Hold Status</label>
              <select name="legalHoldStatus" value={formData.legalHoldStatus} onChange={handleInputChange} className={inputClass('legalHoldStatus')}>
                {options.legalHoldStatusList?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-6 right-6 z-50 animate-slide-in">
          <div className={`flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl border-l-4 min-w-[320px] max-w-[480px] ${
            toast.type === 'success'
              ? 'bg-white border-l-[#0077b5] border-[#0077b5]/20 shadow-[#0077b5]/10'
              : 'bg-white border-l-[#F47F2E] border-[#F47F2E]/20 shadow-[#F47F2E]/10'
          }`}>
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              toast.type === 'success' ? 'bg-[#0077b5]/15' : 'bg-[#F47F2E]/15'
            }`}>
              {toast.type === 'success' ? (
                <svg className="w-5 h-5 text-[#0077b5]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-[#F47F2E]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${
                toast.type === 'success' ? 'text-[#0077b5]' : 'text-[#F47F2E]'
              }`}>
                {toast.type === 'success' ? 'Success' : 'Error'}
              </p>
              <p className="text-sm text-[#53545a] mt-0.5 leading-snug">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ ...toast, show: false })}
              className="flex-shrink-0 p-1 rounded-lg text-[#919191] hover:text-[#53545a] hover:bg-[#919191]/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page header skeleton for quick visual while loading */}
      {loading && (
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              {/* <div className="w-40 h-7 bg-[#e6eef5] rounded animate-pulse mb-2" /> */}
              <div className="w-72 h-7 bg-[#e6eef5] rounded animate-pulse" />
            </div>
            <div className="w-40 h-10 bg-[#e6eef5] rounded animate-pulse" />
          </div>
        </div>
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={organizations}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={fetchOrganizations}
        searchable
        searchPlaceholder="Search organizations"
        actions={isAssessmentAdmin ? undefined : getActions}
        headerActions={
          <div className="flex items-center gap-3">
            {/* Active / Deactivated view toggle */}
            <div className="inline-flex rounded-lg border border-[#919191]/30 p-0.5 bg-white">
              <button
                onClick={() => setViewMode('active')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'active' ? 'bg-[#F47F2E] text-white' : 'text-[#53545a] hover:bg-[#919191]/10'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setViewMode('deactivated')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'deactivated' ? 'bg-[#F47F2E] text-white' : 'text-[#53545a] hover:bg-[#919191]/10'
                }`}
              >
                Deactivated
              </button>
            </div>
            {!isAssessmentAdmin && viewMode === 'active' && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors shadow-sm"
              >
                <FiPlus className="text-base" /> Add Organization
              </button>
            )}
          </div>
        }
        totalLabel="organizations"
        emptyMessage="No organizations found"
      />

      {/* Create/Edit Modal with Step Wizard */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditing ? "Edit Organization" : "Add New Organization"}
        size="xl"
        footer={
          <div className="flex justify-between w-full">
            <div>
              {currentStep > 0 && (
                <button
                  onClick={() => { setValidationErrors({}); setCurrentStep(s => s - 1); }}
                  className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
                >
                  <FiChevronLeft /> Previous
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setIsModalOpen(false); setCurrentStep(0); }}
                className={`px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
              >
                Cancel
              </button>
              {currentStep < formSteps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
                >
                  Next <FiChevronRight />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
                >
                  {isEditing ? 'Save Changes' : 'Create Entity'}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Step Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {formSteps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index < currentStep 
                      ? 'bg-[#0077b5] text-white' 
                      : index === currentStep 
                        ? 'bg-[#F47F2E] text-white' 
                        : 'bg-[#919191]/20 text-[#919191]'
                  }`}>
                    {index < currentStep ? <FiCheck /> : index + 1}
                  </div>
                  <div className="mt-2 text-center hidden sm:block">
                    <p className={`text-xs font-medium text-[#53545a]`}>{step.title}</p>
                    <p className={`text-xs text-[#919191]`}>{step.description}</p>
                  </div>
                </div>
                {index < formSteps.length - 1 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                    index < currentStep 
                      ? 'bg-[#0077b5]' 
                      : 'bg-[#919191]/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className={`p-4 rounded-lg bg-[#919191]/5`}>
          {renderStepContent(currentStep)}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Deactivate Organization"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className={`px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
            >
              Deactivate
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-[#F47F2E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="text-[#F47F2E] text-2xl" />
          </div>
          <h3 className={`text-lg font-medium mb-2 text-[#53545a]`}>Deactivate this organization?</h3>
          <p className="text-[#919191]">
            <span className={`font-medium text-[#53545a]`}>{selectedOrg?.name}</span> will be deactivated and
            hidden, along with its assessments. It will be{' '}
            <span className="font-medium text-[#53545a]">permanently deleted after 30 days</span> unless you
            restore it from the <span className="font-medium text-[#53545a]">Deactivated</span> view before then.
          </p>
        </div>
      </Modal>

    </div>
  );
};

export default Organizations;
